import React, { useState, useEffect, useCallback } from 'react';
import { 
  BarChart3, Users, MousePointerClick, Clock, TrendingUp, Eye, ShoppingCart,
  QrCode, ArrowUpRight, ArrowDownRight, RefreshCw, Calendar, Filter,
  Activity, Globe, Smartphone, Monitor, Target, Zap, PieChart
} from 'lucide-react';
import {
  LineChart, Line, BarChart, Bar, PieChart as RePieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, AreaChart, Area
} from 'recharts';
import api from '../../../utils/apiClient';

const StatCard = ({ title, value, change, icon: Icon, color = 'teal', suffix = '', prefix = '' }) => {
  const isPositive = change >= 0;
  const colorClasses = {
    teal: 'bg-teal-50 dark:bg-teal-900/20 text-teal-600 dark:text-teal-400',
    blue: 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400',
    green: 'bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400',
    amber: 'bg-amber-50 dark:bg-amber-900/20 text-amber-600 dark:text-amber-400',
    purple: 'bg-purple-50 dark:bg-purple-900/20 text-purple-600 dark:text-purple-400',
    red: 'bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400',
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl p-5 shadow-sm border border-gray-100 dark:border-gray-700">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">{title}</p>
          <p className="text-2xl font-bold text-gray-900 dark:text-white">
            {prefix}{typeof value === 'number' ? value.toLocaleString() : value}{suffix}
          </p>
          {change !== undefined && (
            <div className={`flex items-center gap-1 mt-2 text-sm ${isPositive ? 'text-green-600' : 'text-red-600'}`}>
              {isPositive ? <ArrowUpRight className="h-4 w-4" /> : <ArrowDownRight className="h-4 w-4" />}
              <span>{Math.abs(change)}% vs prev period</span>
            </div>
          )}
        </div>
        <div className={`p-3 rounded-xl ${colorClasses[color]}`}>
          <Icon className="h-6 w-6" />
        </div>
      </div>
    </div>
  );
};

const COLORS = ['#0D9488', '#F59E0B', '#8B5CF6', '#EF4444', '#3B82F6', '#10B981', '#EC4899', '#6366F1'];

const SiteStatisticsTab = ({ token }) => {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [days, setDays] = useState(30);
  const [activeSection, setActiveSection] = useState('overview');
  
  const [overview, setOverview] = useState(null);
  const [pageAnalytics, setPageAnalytics] = useState(null);
  const [qrAnalytics, setQrAnalytics] = useState(null);
  const [conversionAnalytics, setConversionAnalytics] = useState(null);
  const [cartAnalytics, setCartAnalytics] = useState(null);
  const [realtimeData, setRealtimeData] = useState(null);

  const fetchData = useCallback(async () => {
    if (!token) return;
    
    try {
      const [overviewRes, pagesRes, qrRes, convRes, cartRes] = await Promise.all([
        api.get(`/admin/site-statistics/overview?days=${days}`),
        api.get(`/admin/analytics/pages?days=${days}&limit=20`),
        api.get(`/admin/analytics/qr-codes?days=${days}`),
        api.get(`/admin/analytics/conversions?days=${days}`),
        api.get(`/admin/analytics/cart?days=${days}`)
      ]);

      setOverview(overviewRes.data);
      setPageAnalytics(pagesRes.data);
      setQrAnalytics(qrRes.data);
      setConversionAnalytics(convRes.data);
      setCartAnalytics(cartRes.data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [token, days]);

  const fetchRealtime = useCallback(async () => {
    if (!token) return;
    try {
      const res = await api.get('/admin/analytics/realtime');
      setRealtimeData(res.data);
    } catch (error) {
      console.error('Error fetching realtime data:', error);
    }
  }, [token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    // Fetch realtime data every 30 seconds
    fetchRealtime();
    const interval = setInterval(fetchRealtime, 30000);
    return () => clearInterval(interval);
  }, [fetchRealtime]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchData();
    fetchRealtime();
  };

  const formatDuration = (seconds) => {
    if (!seconds || seconds < 0) return '0s';
    if (seconds < 60) return `${Math.round(seconds)}s`;
    const mins = Math.floor(seconds / 60);
    const secs = Math.round(seconds % 60);
    return `${mins}m ${secs}s`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-teal-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="site-statistics-tab">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Site Statistics</h2>
          <p className="text-gray-500 dark:text-gray-400 text-sm mt-1">
            Track visitor behavior, conversions, and site performance
          </p>
        </div>
        <div className="flex items-center gap-3">
          <select
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            className="px-3 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm"
          >
            <option value={7}>Last 7 days</option>
            <option value={14}>Last 14 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
            <option value={365}>Last year</option>
          </select>
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="p-2 bg-teal-600 hover:bg-teal-700 text-white rounded-lg transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`h-5 w-5 ${refreshing ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Section Tabs */}
      <div className="flex gap-2 border-b border-gray-200 dark:border-gray-700 overflow-x-auto pb-px">
        {[
          { id: 'overview', label: 'Overview', icon: BarChart3 },
          { id: 'pages', label: 'Pages', icon: Eye },
          { id: 'conversions', label: 'Conversions', icon: Target },
          { id: 'cart', label: 'Cart & Orders', icon: ShoppingCart },
          { id: 'qr', label: 'QR Codes', icon: QrCode },
          { id: 'realtime', label: 'Real-time', icon: Zap },
        ].map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            onClick={() => setActiveSection(id)}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
              activeSection === id
                ? 'border-teal-600 text-teal-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
            }`}
          >
            <Icon className="h-4 w-4" />
            {label}
          </button>
        ))}
      </div>

      {/* Overview Section */}
      {activeSection === 'overview' && overview && (
        <div className="space-y-6">
          {/* Stat Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <StatCard
              title="Total Page Views"
              value={overview.total_pageviews}
              icon={Eye}
              color="teal"
            />
            <StatCard
              title="Unique Visitors"
              value={overview.unique_visitors}
              icon={Users}
              color="blue"
            />
            <StatCard
              title="Conversions"
              value={overview.total_conversions}
              icon={Target}
              color="green"
            />
            <StatCard
              title="Conversion Rate"
              value={overview.conversion_rate}
              suffix="%"
              icon={TrendingUp}
              color="purple"
            />
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <StatCard
              title="Avg. Session Duration"
              value={formatDuration(overview.avg_session_duration)}
              icon={Clock}
              color="amber"
            />
            <StatCard
              title="Bounce Rate"
              value={overview.bounce_rate}
              suffix="%"
              icon={ArrowUpRight}
              color="red"
            />
            <StatCard
              title="QR Code Scans"
              value={overview.qr_code_scans}
              icon={QrCode}
              color="purple"
            />
            <StatCard
              title="Sessions"
              value={overview.unique_sessions}
              icon={Activity}
              color="teal"
            />
          </div>

          {/* Traffic Chart */}
          <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-100 dark:border-gray-700">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Traffic Over Time</h3>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={overview.daily_stats || []}>
                  <defs>
                    <linearGradient id="colorViews" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#0D9488" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#0D9488" stopOpacity={0}/>
                    </linearGradient>
                    <linearGradient id="colorVisitors" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#3B82F6" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.2} />
                  <XAxis dataKey="date" tick={{ fontSize: 12 }} stroke="#9CA3AF" />
                  <YAxis tick={{ fontSize: 12 }} stroke="#9CA3AF" />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'rgba(17, 24, 39, 0.9)', 
                      border: 'none', 
                      borderRadius: '8px',
                      color: '#fff'
                    }} 
                  />
                  <Legend />
                  <Area type="monotone" dataKey="pageviews" name="Page Views" stroke="#0D9488" fillOpacity={1} fill="url(#colorViews)" />
                  <Area type="monotone" dataKey="unique_visitors" name="Unique Visitors" stroke="#3B82F6" fillOpacity={1} fill="url(#colorVisitors)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Top Pages & Traffic Sources */}
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-100 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Top Pages</h3>
              <div className="space-y-3">
                {(overview.top_pages || []).slice(0, 8).map((page, idx) => (
                  <div key={idx} className="flex items-center justify-between">
                    <div className="flex items-center gap-3 min-w-0">
                      <span className="text-sm font-medium text-gray-500 w-5">{idx + 1}</span>
                      <span className="text-sm text-gray-900 dark:text-white truncate">{page.page || page._id}</span>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className="text-sm text-gray-600 dark:text-gray-400">{page.views} views</span>
                      <span className="text-xs text-gray-500">{page.unique_visitors} visitors</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-100 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Traffic Sources</h3>
              <div className="space-y-3">
                {(overview.traffic_sources || []).slice(0, 8).map((source, idx) => (
                  <div key={idx} className="flex items-center justify-between">
                    <div className="flex items-center gap-3 min-w-0">
                      <Globe className="h-4 w-4 text-gray-400" />
                      <span className="text-sm text-gray-900 dark:text-white truncate">
                        {source._id || 'Direct'}
                      </span>
                    </div>
                    <span className="text-sm text-gray-600 dark:text-gray-400">{source.visits} visits</span>
                  </div>
                ))}
                {(!overview.traffic_sources || overview.traffic_sources.length === 0) && (
                  <p className="text-sm text-gray-500 text-center py-4">No referrer data yet</p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Pages Section */}
      {activeSection === 'pages' && pageAnalytics && (
        <div className="space-y-6">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden">
            <div className="p-4 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Page Performance</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 dark:bg-gray-900/50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Page</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Views</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Visitors</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Avg. Time</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Scroll Depth</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {(pageAnalytics.pages || []).map((page, idx) => (
                    <tr key={idx} className="hover:bg-gray-50 dark:hover:bg-gray-900/30">
                      <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                        {page.page_path}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400 text-right">
                        {page.total_views?.toLocaleString()}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400 text-right">
                        {page.unique_visitors?.toLocaleString()}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400 text-right">
                        {formatDuration(page.avg_time_on_page)}
                      </td>
                      <td className="px-4 py-3 text-sm text-right">
                        <div className="flex items-center justify-end gap-2">
                          <div className="w-20 bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                            <div 
                              className="bg-teal-600 h-2 rounded-full" 
                              style={{ width: `${page.avg_scroll_depth || 0}%` }}
                            />
                          </div>
                          <span className="text-gray-600 dark:text-gray-400">{page.avg_scroll_depth || 0}%</span>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Conversions Section */}
      {activeSection === 'conversions' && conversionAnalytics && (
        <div className="space-y-6">
          {/* Funnel */}
          <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-100 dark:border-gray-700">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Conversion Funnel</h3>
            <div className="grid grid-cols-4 gap-4">
              {[
                { label: 'Visitors', value: conversionAnalytics.funnel?.visitors || 0, color: 'bg-blue-500' },
                { label: 'Added to Cart', value: conversionAnalytics.funnel?.cart_adds || 0, rate: conversionAnalytics.funnel?.cart_rate, color: 'bg-amber-500' },
                { label: 'Checkout', value: conversionAnalytics.funnel?.checkouts || 0, rate: conversionAnalytics.funnel?.checkout_rate, color: 'bg-purple-500' },
                { label: 'Purchased', value: conversionAnalytics.funnel?.purchases || 0, rate: conversionAnalytics.funnel?.purchase_rate, color: 'bg-green-500' },
              ].map((step, idx) => (
                <div key={idx} className="text-center">
                  <div className={`h-24 ${step.color} rounded-lg flex items-center justify-center mb-2`} 
                       style={{ opacity: 0.3 + (0.7 * (1 - idx / 4)) }}>
                    <span className="text-2xl font-bold text-white">{step.value.toLocaleString()}</span>
                  </div>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">{step.label}</p>
                  {step.rate !== undefined && (
                    <p className="text-xs text-gray-500">{step.rate}% rate</p>
                  )}
                </div>
              ))}
            </div>
            <div className="mt-4 text-center">
              <span className="text-lg font-semibold text-teal-600">
                Overall Conversion: {conversionAnalytics.funnel?.overall_conversion || 0}%
              </span>
            </div>
          </div>

          {/* Conversions by Type */}
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-100 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">By Type</h3>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <RePieChart>
                    <Pie
                      data={conversionAnalytics.conversions_by_type || []}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={90}
                      dataKey="count"
                      nameKey="_id"
                      label={({ _id, count }) => `${_id}: ${count}`}
                    >
                      {(conversionAnalytics.conversions_by_type || []).map((_, idx) => (
                        <Cell key={idx} fill={COLORS[idx % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </RePieChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-100 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">By Page</h3>
              <div className="space-y-3">
                {(conversionAnalytics.conversions_by_page || []).map((item, idx) => (
                  <div key={idx} className="flex items-center justify-between">
                    <span className="text-sm text-gray-900 dark:text-white truncate">{item.page}</span>
                    <span className="text-sm font-medium text-teal-600">{item.conversions} conversions</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Cart & Orders Section */}
      {activeSection === 'cart' && cartAnalytics && (
        <div className="space-y-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <StatCard
              title="Total Orders"
              value={cartAnalytics.total_orders || 0}
              icon={ShoppingCart}
              color="green"
            />
            <StatCard
              title="Total Revenue"
              value={cartAnalytics.total_revenue || 0}
              prefix="$"
              icon={TrendingUp}
              color="teal"
            />
            <StatCard
              title="Avg. Order Value"
              value={cartAnalytics.avg_order_value || 0}
              prefix="$"
              icon={BarChart3}
              color="blue"
            />
            <StatCard
              title="Abandonment Rate"
              value={cartAnalytics.abandonment_rate || 0}
              suffix="%"
              icon={ArrowDownRight}
              color="red"
            />
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {/* Top Items */}
            <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-100 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Most Added to Cart</h3>
              <div className="space-y-3">
                {(cartAnalytics.top_items_added || []).map((item, idx) => (
                  <div key={idx} className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span className="w-6 h-6 bg-teal-100 dark:bg-teal-900/30 rounded-full flex items-center justify-center text-xs font-medium text-teal-600">
                        {idx + 1}
                      </span>
                      <span className="text-sm text-gray-900 dark:text-white">{item.item_name || 'Unknown'}</span>
                    </div>
                    <span className="text-sm text-gray-600 dark:text-gray-400">{item.adds} adds</span>
                  </div>
                ))}
                {(!cartAnalytics.top_items_added || cartAnalytics.top_items_added.length === 0) && (
                  <p className="text-sm text-gray-500 text-center py-4">No cart data yet</p>
                )}
              </div>
            </div>

            {/* Cart Events */}
            <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-100 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Cart Events</h3>
              <div className="h-48">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={cartAnalytics.cart_events || []} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.2} />
                    <XAxis type="number" tick={{ fontSize: 12 }} stroke="#9CA3AF" />
                    <YAxis dataKey="_id" type="category" tick={{ fontSize: 11 }} stroke="#9CA3AF" width={100} />
                    <Tooltip />
                    <Bar dataKey="count" fill="#0D9488" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          {/* Abandoned Cart Value */}
          <div className="bg-gradient-to-r from-red-500/10 to-amber-500/10 border border-red-200 dark:border-red-900/30 rounded-xl p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-red-100 dark:bg-red-900/30 rounded-xl">
                <ShoppingCart className="h-8 w-8 text-red-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Abandoned Cart Value</p>
                <p className="text-3xl font-bold text-red-600">${(cartAnalytics.total_abandoned_value || 0).toFixed(2)}</p>
                <p className="text-sm text-gray-500">{cartAnalytics.abandoned_carts_count || 0} abandoned carts</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* QR Codes Section */}
      {activeSection === 'qr' && qrAnalytics && (
        <div className="space-y-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <StatCard
              title="Total QR Scans"
              value={qrAnalytics.total_scans || 0}
              icon={QrCode}
              color="purple"
            />
            <StatCard
              title="Unique Visitors"
              value={qrAnalytics.unique_visitors || 0}
              icon={Users}
              color="blue"
            />
            <StatCard
              title="Conversions"
              value={qrAnalytics.conversions || 0}
              icon={Target}
              color="green"
            />
            <StatCard
              title="Conversion Rate"
              value={qrAnalytics.conversion_rate || 0}
              suffix="%"
              icon={TrendingUp}
              color="teal"
            />
          </div>

          {/* QR Stats Table */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden">
            <div className="p-4 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">QR Code Performance</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 dark:bg-gray-900/50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Landing Page</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Campaign</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Scans</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Unique Visitors</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {(qrAnalytics.qr_stats || []).map((stat, idx) => (
                    <tr key={idx} className="hover:bg-gray-50 dark:hover:bg-gray-900/30">
                      <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">{stat.page_path}</td>
                      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">{stat.campaign || '-'}</td>
                      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400 text-right">{stat.scans}</td>
                      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400 text-right">{stat.unique_visitors}</td>
                    </tr>
                  ))}
                  {(!qrAnalytics.qr_stats || qrAnalytics.qr_stats.length === 0) && (
                    <tr>
                      <td colSpan="4" className="px-4 py-8 text-center text-gray-500">
                        No QR code scans recorded yet
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Real-time Section */}
      {activeSection === 'realtime' && (
        <div className="space-y-6">
          <div className="bg-gradient-to-r from-green-500/10 to-teal-500/10 border border-green-200 dark:border-green-900/30 rounded-xl p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="relative">
                  <div className="p-3 bg-green-100 dark:bg-green-900/30 rounded-xl">
                    <Activity className="h-8 w-8 text-green-600" />
                  </div>
                  <span className="absolute -top-1 -right-1 w-3 h-3 bg-green-500 rounded-full animate-pulse" />
                </div>
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Active Visitors (last 30 min)</p>
                  <p className="text-4xl font-bold text-green-600">{realtimeData?.active_visitors || 0}</p>
                </div>
              </div>
              <p className="text-xs text-gray-500">
                Last updated: {realtimeData?.updated_at ? new Date(realtimeData.updated_at).toLocaleTimeString() : '-'}
              </p>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {/* Active Pages */}
            <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-100 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Currently Viewing</h3>
              <div className="space-y-3">
                {(realtimeData?.active_pages || []).map((page, idx) => (
                  <div key={idx} className="flex items-center justify-between">
                    <span className="text-sm text-gray-900 dark:text-white">{page.page}</span>
                    <span className="px-2 py-1 bg-green-100 dark:bg-green-900/30 text-green-600 text-xs font-medium rounded-full">
                      {page.active_visitors} active
                    </span>
                  </div>
                ))}
                {(!realtimeData?.active_pages || realtimeData.active_pages.length === 0) && (
                  <p className="text-sm text-gray-500 text-center py-4">No active visitors</p>
                )}
              </div>
            </div>

            {/* Recent Activity */}
            <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-100 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Recent Activity</h3>
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {(realtimeData?.recent_events || []).slice(0, 10).map((event, idx) => (
                  <div key={idx} className="flex items-center gap-3 text-sm py-1">
                    <MousePointerClick className="h-4 w-4 text-gray-400" />
                    <span className="text-gray-600 dark:text-gray-400">{event.event_type}</span>
                    <span className="text-gray-500">on</span>
                    <span className="text-gray-900 dark:text-white">{event.page_path}</span>
                  </div>
                ))}
                {(!realtimeData?.recent_events || realtimeData.recent_events.length === 0) && (
                  <p className="text-sm text-gray-500 text-center py-4">No recent activity</p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SiteStatisticsTab;
